/**
 * StudioSuperAdmin — Cross-user, cross-channel god view for super_admin.
 *
 * Tabs: Overview | Channels | Users | Audit Trail. Includes impersonation.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Shield, ArrowLeft, Users, Youtube, Activity, Loader2, AlertTriangle,
  Eye, Search, RefreshCw, UserCheck, LogOut, Crown,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

export default function StudioSuperAdmin() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [error, setError] = useState(null);
  const [accessUser, setAccessUser] = useState(null); // {id, email, name}
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showBulk, setShowBulk] = useState(false);

  const load = async (which = tab) => {
    setLoading(true); setError(null);
    try {
      if (which === "overview") {
        const r = await axios.get(`${API}/api/studio/super/overview`, { withCredentials: true });
        setOverview(r.data);
      } else if (which === "channels") {
        const r = await axios.get(`${API}/api/studio/super/channels${q ? `?q=${encodeURIComponent(q)}` : ""}`, { withCredentials: true });
        setChannels(r.data?.channels || []);
      } else if (which === "users") {
        const r = await axios.get(`${API}/api/studio/super/users${q ? `?q=${encodeURIComponent(q)}` : ""}`, { withCredentials: true });
        setUsers(r.data?.users || []);
      } else if (which === "audit") {
        const r = await axios.get(`${API}/api/studio/super/audit`, { withCredentials: true });
        setAudit(r.data?.audit || []);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Forbidden");
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [tab]);

  // Preload channels list once so the user-access modal has options available.
  useEffect(() => {
    if (channels.length) return;
    axios
      .get(`${API}/api/studio/super/channels`, { withCredentials: true })
      .then((r) => setChannels(r.data?.channels || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const impersonate = async (userId, email) => {
    if (!window.confirm(`Login as ${email}? You can exit anytime.`)) return;
    try {
      await axios.post(`${API}/api/studio/super/impersonate`, { target_user_id: userId }, { withCredentials: true });
      toast.success(`Now viewing as ${email}`);
      window.location.href = "/studio";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="super-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <Crown size={12} /> Super Admin Console
          </span>
          <h1 className="studio-h1">
            God view <span className="studio-h1-accent">across every account.</span>
          </h1>
          <p className="studio-sub">
            singadkamlesh1112@gmail.com privileges: see all channels, all users, all activity. Grant access. Impersonate. Audit every action.
          </p>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {[
              { id: "overview", icon: Shield, label: "Overview" },
              { id: "channels", icon: Youtube, label: "All channels" },
              { id: "users", icon: Users, label: "All users" },
              { id: "audit", icon: Activity, label: "Audit trail" },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  className={tab === t.id ? "studio-btn-primary" : "studio-btn-ghost"}
                  onClick={() => setTab(t.id)}
                  data-testid={`super-tab-${t.id}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="ac-error" data-testid="super-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {loading && <div className="studio-skeleton studio-sk-card" style={{ height: 120 }} />}

        {!loading && tab === "overview" && overview && (
          <div data-testid="super-overview">
            <div className="studio-ops-row">
              {[
                { label: "Users", val: overview.totals.users, icon: Users },
                { label: "Channels", val: overview.totals.channels, icon: Youtube },
                { label: "Subscribers", val: fmtNum(overview.totals.subscribers), icon: Users },
                { label: "Total views", val: fmtNum(overview.totals.views), icon: Eye },
                { label: "Content", val: overview.totals.content, icon: Activity },
                { label: "Connected", val: overview.totals.connected_accounts, icon: UserCheck },
              ].map((it) => {
                const Icon = it.icon;
                return (
                  <div key={it.label} className="studio-ops-card" data-testid={`super-stat-${it.label.toLowerCase().replace(/\W+/g,'-')}`}>
                    <header><span className="studio-eyebrow"><Icon size={12} /> {it.label}</span></header>
                    <p style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 0" }}>{it.val ?? 0}</p>
                  </div>
                );
              })}
            </div>
            <div className="studio-ops-row" style={{ marginTop: 14 }}>
              {[
                { label: "Pending today", val: overview.today.pending, color: "#a78bfa" },
                { label: "Delayed", val: overview.today.delayed, color: "#ef4444" },
                { label: "Scheduled today", val: overview.today.scheduled, color: "#10b981" },
                { label: "Published yesterday", val: overview.yesterday.published, color: "#0ea5e9" },
              ].map((it) => (
                <div key={it.label} className="studio-ops-card" data-testid={`super-today-${it.label.toLowerCase().replace(/\W+/g,'-')}`}>
                  <header><span className="studio-eyebrow">{it.label}</span></header>
                  <p style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 0", color: it.color }}>{it.val ?? 0}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && tab === "channels" && (
          <div className="studio-card" style={{ padding: 18 }} data-testid="super-channels">
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input className="studio-input" placeholder="Search by title…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load("channels")} data-testid="super-channels-search" />
              <button className="studio-btn-primary" onClick={() => load("channels")} data-testid="super-channels-refresh"><RefreshCw size={14} /> Refresh</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #F8FAFC", textAlign: "left" }}>
                    <th style={{ padding: 8 }}>Channel</th>
                    <th style={{ padding: 8 }}>Owner</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Subs</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Views</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Members</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Content</th>
                    <th style={{ padding: 8 }}>Timezone</th>
                    <th style={{ padding: 8 }}>OAuth</th>
                  </tr>
                </thead>
                <tbody>{channels.map((c) => (
                    <tr key={c.channel_id} style={{ borderBottom: "1px dashed #F8FAFC" }} data-testid={`super-channel-${c.channel_id}`}>
                      <td style={{ padding: 8, fontWeight: 600 }}>{c.title || "—"}</td>
                      <td style={{ padding: 8 }}>{c.owner_email || c.owner_name || (c.owner_id ? c.owner_id.slice(0, 8) : "—")}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmtNum(c.subscriber_count)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{fmtNum(c.view_count)}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{c.members_count}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{c.content_count}</td>
                      <td style={{ padding: 8 }}><ChannelTzEditor channelId={c.channel_id} /></td>
                      <td style={{ padding: 8 }}>
                        <span className={`studio-pill studio-pill--sm studio-pill--${c.oauth_connected ? "ok" : "warn"}`}>
                          {c.oauth_connected ? "Connected" : "Disconnected"}
                        </span>
                      </td>
                    </tr>
                  ))}</tbody>
              </table>
              {!channels.length && <p className="studio-muted" style={{ textAlign: "center", padding: 18 }}>No channels found.</p>}
            </div>
          </div>
        )}

        {!loading && tab === "users" && (
          <div className="studio-card" style={{ padding: 18 }} data-testid="super-users">
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
              <input className="studio-input" placeholder="Search by email or name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load("users")} data-testid="super-users-search" style={{ flex: 1, minWidth: 220 }} />
              <button className="studio-btn-primary" onClick={() => load("users")}><Search size={14} /> Search</button>
              <button
                className="studio-btn-primary"
                onClick={() => setShowBulk(true)}
                disabled={!selectedUserIds.length}
                data-testid="super-bulk-open"
                style={{ background: selectedUserIds.length ? "linear-gradient(90deg, #a78bfa, #ec7c5e)" : undefined }}
              >
                <Shield size={14} /> Bulk grant ({selectedUserIds.length})
              </button>
              <button
                className="studio-btn-ghost"
                onClick={() => setShowBulk(true)}
                data-testid="super-bulk-csv-open"
                title="Upload a CSV — works even without selecting users"
              >
                <Shield size={14} /> CSV upload
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #F8FAFC", textAlign: "left" }}>
                    <th style={{ padding: 8, width: 28 }}>
                      <input
                        type="checkbox"
                        data-testid="super-users-select-all"
                        checked={users.length > 0 && selectedUserIds.length === users.length}
                        onChange={(e) => setSelectedUserIds(e.target.checked ? users.map((u) => u.id) : [])}
                      />
                    </th>
                    <th style={{ padding: 8 }}>Email</th>
                    <th style={{ padding: 8 }}>Name</th>
                    <th style={{ padding: 8 }}>Role</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Channels</th>
                    <th style={{ padding: 8, textAlign: "right" }}>Memberships</th>
                    <th style={{ padding: 8 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const checked = selectedUserIds.includes(u.id);
                    return (
                    <tr key={u.id} style={{ borderBottom: "1px dashed #F8FAFC", background: checked ? "rgba(167,139,250,0.06)" : undefined }} data-testid={`super-user-${u.id}`}>
                      <td style={{ padding: 8 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setSelectedUserIds((s) => e.target.checked ? [...s, u.id] : s.filter((x) => x !== u.id))}
                          data-testid={`super-user-pick-${u.id}`}
                        />
                      </td>
                      <td style={{ padding: 8, fontWeight: 600 }}>{u.email}</td>
                      <td style={{ padding: 8 }}>{u.name || "—"}</td>
                      <td style={{ padding: 8 }}>
                        <span className="studio-pill studio-pill--sm">{u.role || "user"}</span>
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>{u.channels_owned}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{u.memberships}</td>
                      <td style={{ padding: 8 }}>
                        <button
                          className="studio-btn-ghost"
                          onClick={() => setAccessUser(u)}
                          data-testid={`super-access-${u.id}`}
                          style={{ padding: "4px 10px", fontSize: 11, marginRight: 6 }}
                        >
                          <Shield size={11} /> Access
                        </button>
                        <button
                          className="studio-btn-ghost"
                          onClick={() => impersonate(u.id, u.email)}
                          data-testid={`super-impersonate-${u.id}`}
                          style={{ padding: "4px 10px", fontSize: 11 }}
                        >
                          <UserCheck size={11} /> Login as
                        </button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
              {!users.length && <p className="studio-muted" style={{ textAlign: "center", padding: 18 }}>No users.</p>}
            </div>
          </div>
        )}

        {!loading && tab === "audit" && (          <div className="studio-card" style={{ padding: 18 }} data-testid="super-audit">
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
              {audit.map((a, i) => (
                <motion.li
                  key={`${a.ts}-${i}`}
                  initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.02 }}
                  style={{ padding: "8px 0", borderBottom: "1px dashed #F8FAFC", fontSize: 13 }}
                  data-testid={`super-audit-${i}`}
                >
                  <span className="studio-pill studio-pill--sm" style={{ marginRight: 8 }}>{a.source || "system"}</span>
                  <strong>{a.actor_email || a.actor_name || a.actor_id?.slice(0, 8) || "system"}</strong>
                  {" "}<em style={{ color: "var(--st-text-soft)" }}>{a.action || a.field}</em>
                  {a.content_title && <> on “<strong>{a.content_title}</strong>”</>}
                  {a.channel_id && <small className="studio-muted"> · {a.channel_id.slice(-6)}</small>}
                  {a.detail && <span style={{ color: "var(--st-text-soft)" }}> · {a.detail}</span>}
                  <small className="studio-muted" style={{ float: "right" }}>
                    {a.ts ? new Date(a.ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}
                  </small>
                </motion.li>
              ))}
            </ul>
            {!audit.length && <p className="studio-muted" style={{ textAlign: "center", padding: 18 }}>No audit events yet.</p>}
          </div>
        )}

        {accessUser && (
          <UserAccessModal
            user={accessUser}
            allChannels={channels}
            onClose={() => setAccessUser(null)}
          />
        )}

        {showBulk && (
          <BulkGrantModal
            users={users.filter((u) => selectedUserIds.includes(u.id))}
            allChannels={channels}
            onClose={() => setShowBulk(false)}
            onDone={() => { setShowBulk(false); setSelectedUserIds([]); load("users"); }}
          />
        )}
      </div>
      <Footer />
    </>
  );
}

/** ImpersonationBanner — render at top of layout when active. */
export function ImpersonationBanner() {
  const [info, setInfo] = useState(null);
  const location = useLocation();
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/studio/super/whoami`, { withCredentials: true })
      .then((r) => { if (alive) setInfo(r.data?.impersonating ? r.data?.by : null); })
      .catch(() => { if (alive) setInfo(null); });
    return () => { alive = false; };
  }, [location.pathname]);
  if (!info) return null;
  const exit = async () => {
    try {
      await axios.post(`${API}/api/studio/super/exit-impersonation`, {}, { withCredentials: true });
      toast.success("Restored super admin session");
      window.location.href = "/studio/super";
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div
      data-testid="impersonation-banner"
      style={{
        position: "fixed", top: 64, left: 0, right: 0, zIndex: 80,
        background: "linear-gradient(90deg, #f59e0b, #ef4444)",
        color: "#fff", padding: "8px 16px",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
        fontSize: 13, fontWeight: 600,
      }}
    >
      <Eye size={14} /> Impersonation active — you are signed in via {info.by_email || "super admin"}
      <button
        onClick={exit}
        data-testid="impersonation-exit"
        style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 700 }}
      >
        <LogOut size={11} /> Exit
      </button>
    </div>
  );
}



const TZ_PRESETS = [
  { label: "UTC", value: 0 },
  { label: "IST (+5)", value: 5 },
  { label: "EST (-5)", value: -5 },
  { label: "PST (-8)", value: -8 },
  { label: "CET (+1)", value: 1 },
  { label: "JST (+9)", value: 9 },
  { label: "SGT (+8)", value: 8 },
];

function ChannelTzEditor({ channelId }) {
  const [tz, setTz] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/studio/notif/channels/${channelId}/timezone`, { withCredentials: true })
      .then((r) => { if (alive) setTz(r.data?.timezone_offset_hours ?? 0); })
      .catch(() => { if (alive) setTz(0); });
    return () => { alive = false; };
  }, [channelId]);
  if (tz === null) return <small className="studio-muted">…</small>;
  const save = async (val) => {
    setSaving(true);
    try {
      const preset = TZ_PRESETS.find((p) => p.value === Number(val));
      await axios.put(`${API}/api/studio/notif/channels/${channelId}/timezone`,
        { timezone_offset_hours: Number(val), timezone_label: preset?.label },
        { withCredentials: true },
      );
      setTz(Number(val));
      toast.success("Timezone saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <select
      className="studio-input"
      value={String(tz)}
      onChange={(e) => save(e.target.value)}
      disabled={saving}
      data-testid={`super-channel-tz-${channelId}`}
      style={{ padding: "4px 8px", fontSize: 11, minWidth: 110 }}
    >
      {TZ_PRESETS.map((p) => (
        <option key={p.label} value={String(p.value)}>{p.label}</option>
      ))}
    </select>
  );
}



const CHANNEL_ROLES = [
  "owner", "manager", "creator", "editor", "designer",
  "seo", "smm", "viewer", "client",
];

function UserAccessModal({ user, allChannels, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickChannel, setPickChannel] = useState("");
  const [pickRole, setPickRole] = useState("editor");

  const load = async () => {
    try {
      const r = await axios.get(
        `${API}/api/studio/super/users/${user.id}/access`,
        { withCredentials: true }
      );
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user.id]);

  const grant = async () => {
    if (!pickChannel) { toast.error("Pick a channel"); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/studio/super/channels/${pickChannel}/access`,
        { user_id: user.id, role: pickRole },
        { withCredentials: true },
      );
      toast.success(`${user.name || user.email} → ${pickRole}`);
      setPickChannel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const revoke = async (channelId) => {
    if (!window.confirm("Remove access from this channel?")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/studio/super/channels/${channelId}/access`,
        { user_id: user.id, role: "remove" },
        { withCredentials: true },
      );
      toast.success("Access removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const owned = data?.owned || [];
  const memberships = data?.memberships || [];
  const usedChannelIds = new Set([
    ...owned.map((c) => c.channel_id),
    ...memberships.map((c) => c.channel_id),
  ]);
  const availableChannels = (allChannels || []).filter(
    (c) => !usedChannelIds.has(c.channel_id)
  );

  return (
    <div
      onClick={onClose}
      data-testid="super-access-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 100,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="studio-card"
        style={{ padding: 22, maxWidth: 620, width: "100%", maxHeight: "80vh", overflowY: "auto" }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <span className="studio-eyebrow"><Shield size={12} /> Channel Access</span>
            <h3 style={{ margin: "4px 0 0" }}>{user.name || user.email}</h3>
            <small className="studio-muted">{user.email}</small>
          </div>
          <button className="studio-btn-ghost" onClick={onClose} data-testid="super-access-close">Close</button>
        </header>

        {!data ? (
          <div className="studio-skeleton studio-sk-card" style={{ height: 80 }} />
        ) : (
          <>
            <h4 style={{ marginTop: 8 }}>Owns</h4>
            {owned.length ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
                {owned.map((c) => (
                  <li key={c.channel_id} style={{ display: "flex", justifyContent: "space-between", padding: 8, borderRadius: 8, background: "#F8FAFC" }}>
                    <span><strong>{c.title}</strong>{c.handle && <small className="studio-muted"> · {c.handle}</small>}</span>
                    <span className="studio-pill studio-pill--sm">owner</span>
                  </li>
                ))}
              </ul>
            ) : <p className="studio-muted" style={{ margin: 0 }}>Doesn’t own any channel.</p>}

            <h4 style={{ marginTop: 16 }}>Member of</h4>
            {memberships.length ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 6 }}>
                {memberships.map((c) => (
                  <li
                    key={c.channel_id}
                    data-testid={`super-access-row-${c.channel_id}`}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 8, borderRadius: 8, background: "#F8FAFC" }}
                  >
                    <span><strong>{c.title}</strong>{c.handle && <small className="studio-muted"> · {c.handle}</small>}</span>
                    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span className="studio-pill studio-pill--sm">{c.role}</span>
                      <button
                        className="studio-link studio-link--danger"
                        onClick={() => revoke(c.channel_id)}
                        disabled={busy}
                        data-testid={`super-access-revoke-${c.channel_id}`}
                        style={{ fontSize: 11 }}
                      >
                        Remove
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="studio-muted" style={{ margin: 0 }}>No shared-channel memberships.</p>}

            <h4 style={{ marginTop: 16 }}>Grant access</h4>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select
                className="studio-input"
                value={pickChannel}
                onChange={(e) => setPickChannel(e.target.value)}
                data-testid="super-access-channel"
                style={{ flex: 1, minWidth: 200 }}
              >
                <option value="">Pick a channel…</option>
                {availableChannels.map((c) => (
                  <option key={c.channel_id} value={c.channel_id}>{c.title || c.channel_id}</option>
                ))}
              </select>
              <select
                className="studio-input"
                value={pickRole}
                onChange={(e) => setPickRole(e.target.value)}
                data-testid="super-access-role"
                style={{ minWidth: 140 }}
              >
                {CHANNEL_ROLES.filter((r) => r !== "owner").map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button
                className="studio-btn-primary"
                onClick={grant}
                disabled={busy || !pickChannel}
                data-testid="super-access-grant"
              >
                {busy ? <Loader2 size={12} className="ac-spin" /> : <Shield size={12} />} Grant
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function BulkGrantModal({ users, allChannels, onClose, onDone }) {
  const [channelIds, setChannelIds] = useState([]);
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const allChannelIds = (allChannels || []).map((c) => c.channel_id);
  const allChecked = channelIds.length > 0 && channelIds.length === allChannelIds.length;

  const toggle = (id) => {
    setChannelIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const submit = async () => {
    if (!channelIds.length) { toast.error("Pick at least one channel"); return; }
    if (!users.length) { toast.error("Pick at least one user"); return; }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/studio/super/bulk-access`,
        {
          user_ids: users.map((u) => u.id),
          channel_ids: channelIds,
          role,
        },
        { withCredentials: true },
      );
      const d = r.data || {};
      toast.success(
        role === "remove"
          ? `Removed ${d.removed} membership(s) across ${d.channels} channel(s)`
          : `Granted ${d.granted} membership(s) — ${d.users} user(s) × ${d.channels} channel(s) as ${d.role}`
      );
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk grant failed");
    } finally { setBusy(false); }
  };

  const totalOps = users.length * channelIds.length;

  // Lightweight CSV parser: header row + one row per record, supports double-quoted
  // values containing commas. Returns an array of {column: value} objects.
  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return [];
    const splitRow = (line) => {
      const out = []; let cur = ""; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') inQ = !inQ;
        else if (c === "," && !inQ) { out.push(cur); cur = ""; }
        else cur += c;
      }
      out.push(cur);
      return out.map((s) => s.trim().replace(/^"|"$/g, ""));
    };
    const headers = splitRow(lines[0]).map((h) => h.toLowerCase());
    return lines.slice(1).map((l) => {
      const cells = splitRow(l);
      const obj = {};
      headers.forEach((h, i) => { obj[h] = cells[i] || ""; });
      return obj;
    });
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    setCsvBusy(true); setCsvResult(null);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) { toast.error("CSV is empty or missing a header row"); return; }
      const r = await axios.post(
        `${API}/api/studio/super/bulk-access/csv`,
        { rows },
        { withCredentials: true },
      );
      setCsvResult(r.data);
      toast.success(`CSV applied — ${r.data?.accepted || 0} grant/remove ops, ${r.data?.skipped?.length || 0} skipped`);
      // Refresh the parent so counts update.
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV upload failed");
    } finally { setCsvBusy(false); }
  };

  return (
    <div
      onClick={onClose}
      data-testid="super-bulk-modal"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 100,
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="studio-card"
        style={{ padding: 22, maxWidth: 680, width: "100%", maxHeight: "80vh", overflowY: "auto" }}
      >
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <span className="studio-eyebrow"><Shield size={12} /> Bulk grant access</span>
            <h3 style={{ margin: "4px 0 0" }}>
              {users.length} user{users.length === 1 ? "" : "s"} × choose channels
            </h3>
            <small className="studio-muted">Overwrites existing role for the selected (user, channel) pairs.</small>
          </div>
          <button className="studio-btn-ghost" onClick={onClose} data-testid="super-bulk-close">Close</button>
        </header>

        {/* CSV upload — alternative to picking users one-by-one */}
        <div data-testid="super-bulk-csv" style={{ padding: 12, marginBottom: 14, borderRadius: 10, background: "rgba(167,139,250,0.05)", border: "1px dashed rgba(167,139,250,0.25)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <strong style={{ fontSize: 13 }}>Or — upload a CSV</strong>
              <div style={{ color: "#64748B", fontSize: 12 }}>
                Header row: <code>email,role,channels[,name]</code> · channels can be <code>*</code> for all, or <code>channel_id|@handle|channel_id2</code> separated by <code>|</code>. Role <code>remove</code> revokes access.
              </div>
            </div>
            <label className="studio-btn-primary" style={{ padding: "8px 14px", fontSize: 12, cursor: csvBusy ? "wait" : "pointer", opacity: csvBusy ? 0.7 : 1 }}>
              {csvBusy ? <Loader2 size={12} className="ac-spin" /> : <Shield size={12} />} {csvBusy ? "Uploading…" : "Choose CSV"}
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsv}
                disabled={csvBusy}
                data-testid="super-bulk-csv-input"
                style={{ display: "none" }}
              />
            </label>
          </div>
          {csvResult && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#64748B" }} data-testid="super-bulk-csv-result">
              <strong style={{ color: "#10B981" }}>{csvResult.accepted}</strong> applied
              {csvResult.skipped?.length > 0 && (
                <>
                  {" · "}<strong style={{ color: "#F59E0B" }}>{csvResult.skipped.length}</strong> skipped
                  <details style={{ marginTop: 6 }}>
                    <summary style={{ cursor: "pointer" }}>view skipped reasons</summary>
                    <ul style={{ paddingLeft: 18, marginTop: 6 }}>
                      {csvResult.skipped.slice(0, 12).map((s, i) => (
                        <li key={i}>{s.reason} — {JSON.stringify(s.row)}</li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </div>
          )}
        </div>

        <h4 style={{ margin: "8px 0" }}>Selected users</h4>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 90, overflowY: "auto", padding: 6, background: "#F8FAFC", borderRadius: 8 }}>
          {users.map((u) => (
            <span key={u.id} className="studio-pill studio-pill--sm" data-testid={`super-bulk-user-${u.id}`}>
              {u.name || u.email}
            </span>
          ))}
        </div>

        <h4 style={{ margin: "16px 0 8px" }}>Channels</h4>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => setChannelIds(e.target.checked ? allChannelIds : [])}
              data-testid="super-bulk-channel-all"
              style={{ marginRight: 6 }}
            />
            Select all {allChannelIds.length} channel(s)
          </label>
          <small className="studio-muted">{channelIds.length} selected</small>
        </div>
        <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #F8FAFC", borderRadius: 8 }}>
          {(allChannels || []).map((c) => (
            <label
              key={c.channel_id}
              data-testid={`super-bulk-channel-row-${c.channel_id}`}
              style={{
                display: "flex", gap: 10, alignItems: "center", padding: "8px 12px",
                borderBottom: "1px dashed #F8FAFC", cursor: "pointer",
                background: channelIds.includes(c.channel_id) ? "rgba(167,139,250,0.06)" : undefined,
              }}
            >
              <input
                type="checkbox"
                checked={channelIds.includes(c.channel_id)}
                onChange={() => toggle(c.channel_id)}
                data-testid={`super-bulk-channel-pick-${c.channel_id}`}
              />
              <span style={{ flex: 1 }}>
                <strong>{c.title || c.channel_id}</strong>
                {c.owner_email && <small className="studio-muted"> · {c.owner_email}</small>}
              </span>
              <small className="studio-muted">{c.members_count || 0} members</small>
            </label>
          ))}
        </div>

        <h4 style={{ margin: "16px 0 8px" }}>Role to assign</h4>
        <select
          className="studio-input"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          data-testid="super-bulk-role"
          style={{ width: "100%" }}
        >
          {CHANNEL_ROLES.filter((r) => r !== "owner").map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
          <option value="remove">— remove (revoke access) —</option>
        </select>

        <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <small className="studio-muted" data-testid="super-bulk-summary">
            Will {role === "remove" ? "remove" : "grant"} <strong>{totalOps}</strong> membership(s) ·
            {users.length} user(s) × {channelIds.length} channel(s){role !== "remove" && <> as <strong>{role}</strong></>}.
          </small>
          <button
            className="studio-btn-primary"
            onClick={submit}
            disabled={busy || !channelIds.length}
            data-testid="super-bulk-submit"
          >
            {busy ? <Loader2 size={14} className="ac-spin" /> : <Shield size={14} />}
            {role === "remove" ? " Remove access" : " Grant access"}
          </button>
        </div>
      </div>
    </div>
  );
}
